import { useRef, useState } from "react";

import { useAuth } from "../../auth/AuthProvider";
import { ErrorAlert } from "../../components/ErrorAlert";
import { Modal } from "../../components/Modal";

import { GalleryImage, cardLabel } from "./GalleryCard";
import { ReactionBar } from "./ReactionBar";
import { REPORT_REASONS } from "./reactions";

import type { ApiErrorCode } from "../../api/client";
import type {
  GalleryPostCard,
  GalleryReactionState,
  GalleryReportReason,
} from "@canvaschool/contracts";

/** The enlarged view: the picture, the reaction bubbles and 「신고」. */
export const GalleryViewer = ({
  post,
  onClose,
  onReaction,
  onReported,
}: {
  post: GalleryPostCard;
  onClose: () => void;
  onReaction: (state: GalleryReactionState) => void;
  onReported: () => void;
}) => {
  const auth = useAuth();
  const { api } = auth;
  const [reporting, setReporting] = useState(false);
  const [reason, setReason] = useState<GalleryReportReason | null>(null);
  const [sending, setSending] = useState(false);
  const sendingRef = useRef(false);
  const [error, setError] = useState<ApiErrorCode | null>(null);

  const send = async () => {
    if (!reason || sendingRef.current) {
      return;
    }
    sendingRef.current = true;
    setSending(true);
    setError(null);
    try {
      await api.reportGalleryPost(post.id, reason);
      setReporting(false);
      onReported();
    } catch (failure) {
      setError(auth.handleError(failure));
    } finally {
      sendingRef.current = false;
      setSending(false);
    }
  };

  return (
    <Modal
      title={cardLabel(post)}
      primaryLabel="닫기"
      secondaryLabel="닫기"
      hideActions
      className="canvaschool-modal--wide canvaschool-gallery-viewer"
      onPrimary={onClose}
      onCancel={onClose}
    >
      <GalleryImage post={post} eager />
      <ReactionBar
        post={post}
        showLabels
        onChange={onReaction}
        onError={setError}
      />
      {error && <ErrorAlert code={error} />}
      {post.reportedByMe ? (
        <p className="canvaschool-hint" role="status">
          신고했어요
        </p>
      ) : reporting ? (
        <form
          className="canvaschool-gallery-report"
          aria-label="작품 신고"
          onSubmit={(event) => {
            event.preventDefault();
            void send();
          }}
        >
          <fieldset className="canvaschool-fieldset">
            <legend>왜 신고하나요?</legend>
            {REPORT_REASONS.map((item) => (
              <label key={item.reason} className="canvaschool-gallery-reason">
                <input
                  type="radio"
                  name="gallery-report-reason"
                  value={item.reason}
                  checked={reason === item.reason}
                  onChange={() => setReason(item.reason)}
                />
                {item.label}
              </label>
            ))}
          </fieldset>
          <div className="canvaschool-actions">
            <button
              type="button"
              className="canvaschool-btn-secondary"
              disabled={sending}
              onClick={() => {
                setReporting(false);
                setReason(null);
              }}
            >
              취소
            </button>
            <button
              type="submit"
              className="canvaschool-btn-danger"
              disabled={!reason || sending}
            >
              신고 보내기
            </button>
          </div>
        </form>
      ) : (
        <button
          type="button"
          className="canvaschool-btn-secondary"
          onClick={() => setReporting(true)}
        >
          신고
        </button>
      )}
    </Modal>
  );
};
